'use client'

const TOKEN_KEY = 'strapi_token'
const USER_KEY = 'strapi_user'

// Helper function to extract display username (ime_prezime) from full username (ime_prezime_timestamp_random)
export function getDisplayUsername(username: string): string {
  if (!username) return ''
  
  // Username format: ime_prezime_timestamp_random
  // We want to extract: ime_prezime
  // Split by underscore and take first two parts (ime and prezime)
  const parts = username.split('_')
  
  // If we have at least 2 parts, return first two joined with underscore
  // Otherwise return the original username
  if (parts.length >= 2) {
    // Check if the last part is a number (timestamp or random number)
    // If so, we likely have the format with ID, so return first two parts
    const lastPart = parts[parts.length - 1]
    const secondLastPart = parts[parts.length - 2]
    
    // If last two parts are numbers (timestamp and random), return everything before them
    if (parts.length >= 4 && /^\d+$/.test(lastPart) && /^\d+$/.test(secondLastPart)) {
      return parts.slice(0, -2).join('_')
    }
    
    // Otherwise, return first two parts (ime_prezime)
    return parts.slice(0, 2).join('_')
  }
  
  return username
}

export interface User {
  id: number
  documentId?: string
  username: string
  email: string
  ime?: string
  prezime?: string
  confirmed: boolean
  blocked: boolean
  photographers?: number[] | Array<{ id: number }>
  /** Populated when using populate=* on /users/me */
  favorite_services?:
    | number[]
    | Array<{ id: number; documentId?: string; title?: string }>
}

export function setToken(token: string) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TOKEN_KEY, token)
    // Also set cookie for middleware - encode token to handle special characters
    const encodedToken = encodeURIComponent(token)
    const maxAge = 60 * 60 * 24 * 7 // 7 days
    const secure = window.location.protocol === 'https:'
    const secureAttr = secure ? '; Secure' : ''
    document.cookie = `auth-token=${encodedToken}; path=/; max-age=${maxAge}; SameSite=Lax${secureAttr}`
  }
}

export function setAdminEmailCookie(email: string) {
  if (typeof window !== 'undefined') {
    const maxAge = 60 * 60 * 24 * 7 // 7 days
    const secure = window.location.protocol === 'https:'
    const secureAttr = secure ? '; Secure' : ''
    document.cookie = `admin-email=${encodeURIComponent(email)}; path=/; max-age=${maxAge}; SameSite=Lax${secureAttr}`
  }
}

export function clearAdminEmailCookie() {
  if (typeof window !== 'undefined') {
    document.cookie = 'admin-email=; path=/; max-age=0; SameSite=Lax'
  }
}

export function getToken(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(TOKEN_KEY)
  }
  return null
}

export function removeToken() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    // Also remove cookies for middleware
    document.cookie = 'auth-token=; path=/; max-age=0; SameSite=Lax'
    document.cookie = 'admin-email=; path=/; max-age=0; SameSite=Lax'
  }
}

export function setUser(user: User) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  }
}

export function getUser(): User | null {
  if (typeof window !== 'undefined') {
    const userStr = localStorage.getItem(USER_KEY)
    if (userStr) {
      try {
        return JSON.parse(userStr)
      } catch {
        return null
      }
    }
  }
  return null
}

export function isAuthenticated(): boolean {
  return getToken() !== null
}

